import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { createApiUrl } from '../utils/apiConfig';
import DragDropFileUpload from '../components/DragDropFileUpload';
import { uploadAssetFilesToS3, MAX_ASSET_FILE_BYTES } from '../utils/assetS3Upload';
import './EditAsset.css';

const EditAsset = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [asset, setAsset] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    video: null,
    thumbnail: null,
    documents: [],
    resources: []
  });
  const [_fileNames, setFileNames] = useState({
    documents: '',
    resources: ''
  });

  useEffect(() => {
    const fetchAsset = async () => {
      try {
        const response = await axios.get(createApiUrl(`/api/assets/${id}`));
        setAsset(response.data);
        setFormData({
          name: response.data.name,
          description: response.data.description,
          video: null,
          thumbnail: null,
          documents: [],
          resources: []
        });
      } catch (error) {
        console.error('Error fetching asset:', error);
      }
    };

    fetchAsset();

    const handleOnlineStatus = () => {
      setIsOnline(navigator.onLine);
    };

    window.addEventListener('online', handleOnlineStatus);
    window.addEventListener('offline', handleOnlineStatus);

    return () => {
      window.removeEventListener('online', handleOnlineStatus);
      window.removeEventListener('offline', handleOnlineStatus);
    };
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const _handleFileChange = (e) => {
    const { name, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: files
    }));

    if (files.length > 0) {
      const fileNames = Array.from(files).map(file => file.name).join(', ');
      setFileNames(prev => ({
        ...prev,
        [name]: fileNames
      }));
    }
  };

  // File handlers for drag-drop component
  const handleVideoSelect = (file) => {
    setFormData(prev => ({
      ...prev,
      video: file
    }));
    setFileNames(prev => ({
      ...prev,
      video: file.name
    }));
  };

  const handleVideoRemove = () => {
    setFormData(prev => ({
      ...prev,
      video: null
    }));
    setFileNames(prev => ({
      ...prev,
      video: ''
    }));
  };

  const handleThumbnailSelect = (file) => {
    setFormData(prev => ({
      ...prev,
      thumbnail: file
    }));
    setFileNames(prev => ({
      ...prev,
      thumbnail: file.name
    }));
  };

  const handleThumbnailRemove = () => {
    setFormData(prev => ({
      ...prev,
      thumbnail: null
    }));
    setFileNames(prev => ({
      ...prev,
      thumbnail: ''
    }));
  };

  const handleDocumentsSelect = (files) => {
    setFormData(prev => ({
      ...prev,
      documents: files
    }));
    const fileNames = Array.from(files).map(file => file.name).join(', ');
    setFileNames(prev => ({
      ...prev,
      documents: fileNames
    }));
  };

  const handleDocumentsRemove = () => {
    setFormData(prev => ({
      ...prev,
      documents: []
    }));
    setFileNames(prev => ({
      ...prev,
      documents: ''
    }));
  };

  const handleResourcesSelect = (files) => {
    setFormData(prev => ({
      ...prev,
      resources: files
    }));
    const fileNames = Array.from(files).map(file => file.name).join(', ');
    setFileNames(prev => ({
      ...prev,
      resources: fileNames
    }));
  };

  const handleResourcesRemove = () => {
    setFormData(prev => ({
      ...prev,
      resources: []
    }));
    setFileNames(prev => ({
      ...prev,
      resources: ''
    }));
  };

  const handleDeleteDocument = async (index) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      await axios.post(createApiUrl(`/api/delete-document-from-asset/${id}`), { documentIndex: index });
      const response = await axios.get(createApiUrl(`/api/assets/${id}`));
      setAsset(response.data);
    } catch (error) {
      console.error('Error deleting document:', error);
    }
  };

  const handleDeleteThumbnail = async () => {
    if (!window.confirm('Are you sure you want to delete this thumbnail?')) {
      return;
    }

    try {
      await axios.post(createApiUrl(`/api/delete-thumbnail-from-asset/${id}`));
      const response = await axios.get(createApiUrl(`/api/assets/${id}`));
      setAsset(response.data);
    } catch (error) {
      console.error('Error deleting thumbnail:', error);
    }
  };

  const handleDeleteResource = async (index) => {
    if (!window.confirm('Are you sure you want to delete this resource?')) {
      return;
    }

    try {
      await axios.post(createApiUrl(`/api/delete-resource-from-asset/${id}`), { resourceIndex: index });
      const response = await axios.get(createApiUrl(`/api/assets/${id}`));
      setAsset(response.data);
    } catch (error) {
      console.error('Error deleting resource:', error);
    }
  };

  const handleDeleteVideo = async () => {
    if (!window.confirm('Are you sure you want to delete this video?')) {
      return;
    }

    try {
      await axios.post(`/api/delete-video-from-asset/${id}`);
      const response = await axios.get(createApiUrl(`/api/assets/${id}`));
      setAsset(response.data);
    } catch (error) {
      console.error('Error deleting video:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const hasNewFiles =
        formData.video ||
        formData.thumbnail ||
        (formData.documents && formData.documents.length > 0) ||
        (formData.resources && formData.resources.length > 0);

      let uploaded = { video: null, thumbnail: null, documents: [], resources: [] };
      if (hasNewFiles) {
        uploaded = await uploadAssetFilesToS3({
          video: formData.video,
          thumbnail: formData.thumbnail,
          documents: formData.documents,
          resources: formData.resources,
        });
      }

      await axios.post(
        `/api/edit-asset/${id}/complete`,
        {
          name: formData.name,
          description: formData.description,
          video: uploaded.video,
          thumbnail: uploaded.thumbnail,
          documents: uploaded.documents,
          resources: uploaded.resources,
        },
        { withCredentials: true }
      );
      navigate(-1);
    } catch (error) {
      console.error('Error updating asset:', error);
    }
  };

  if (!asset) {
    return <div>Loading...</div>;
  }

  return (
    <div className="edit-asset">
      {!isOnline && (
        <div className="connection-status">You are currently offline</div>
      )}
      
      <h1 className="edit-title">Edit Asset</h1>
      
      <form id="edit-form" onSubmit={handleSubmit}>
        <div className="form-content">
          <div className="form-group">
            <label htmlFor="name" className="form-label">Name:</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="form-control"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="description" className="form-label">Description:</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              className="form-control"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="video" className="form-label">Video:</label>
            <DragDropFileUpload
              onFileSelected={handleVideoSelect}
              onFileRemoved={handleVideoRemove}
              selectedFile={formData.video}
              accept="video/*"
              maxFileSize={MAX_ASSET_FILE_BYTES}
              supportedTypes={['.mp4', '.avi', '.mov', '.wmv']}
              placeholder="Click to upload video or drag and drop"
              description="Supports: MP4, AVI, MOV, WMV"
            />
            {asset.file && asset.file.url && (
              <div className="existing-video">
                <h5>Existing Video:</h5>
                <div className="video-container">
                  <video controls height="100" width="180" controlsList="nodownload">
                    <source src={asset.file.url} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                  <p>{asset.file.url.split('/').pop()}</p>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleDeleteVideo}
                  >
                    Delete Video
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="thumbnail" className="form-label">Video Thumbnail:</label>
            <DragDropFileUpload
              onFileSelected={handleThumbnailSelect}
              onFileRemoved={handleThumbnailRemove}
              selectedFile={formData.thumbnail}
              accept="image/*"
              maxFileSize={5 * 1024 * 1024} // 5MB
              supportedTypes={['.jpg', '.jpeg', '.png', '.gif']}
              placeholder="Click to upload thumbnail or drag and drop"
              description="Supports: JPG, PNG, GIF"
            />
            {asset.video && asset.video.thumbnail && (
              <div className="existing-thumbnail">
                <h5>Existing Thumbnail:</h5>
                <div className="thumbnail-container">
                  <img
                    src={asset.video.thumbnail}
                    alt="Thumbnail"
                    style={{ maxWidth: '100px', maxHeight: '100px' }}
                  />
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleDeleteThumbnail}
                  >
                    Delete Thumbnail
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="documents" className="form-label">Documents:</label>
            <DragDropFileUpload
              onFileSelected={handleDocumentsSelect}
              onFileRemoved={handleDocumentsRemove}
              selectedFile={formData.documents && formData.documents.length > 0 ? formData.documents[0] : null}
              accept=".pdf"
              multiple={true}
              maxFileSize={10 * 1024 * 1024} // 10MB
              supportedTypes={['.pdf']}
              placeholder="Click to upload documents or drag and drop"
              description="Supports: PDF files"
            />
            {asset.documents && asset.documents.length > 0 && (
              <div className="existing-documents">
                <h5>Existing Documents:</h5>
                {asset.documents.map((document, index) => (
                  <div key={index} className="document-item">
                    <p>{document.url.split('/').pop()}</p>
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => handleDeleteDocument(index)}
                    >
                      Delete Document
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="resources" className="form-label">Resources:</label>
            <DragDropFileUpload
              onFileSelected={handleResourcesSelect}
              onFileRemoved={handleResourcesRemove}
              selectedFile={formData.resources && formData.resources.length > 0 ? formData.resources[0] : null}
              accept=".pdf"
              multiple={true}
              maxFileSize={10 * 1024 * 1024} // 10MB
              supportedTypes={['.pdf']}
              placeholder="Click to upload resources or drag and drop"
              description="Supports: PDF files"
            />
            {asset.resources && asset.resources.length > 0 && (
              <div className="existing-resources">
                <h5>Existing Resources:</h5>
                {asset.resources.map((resource, index) => (
                  <div key={index} className="resource-item">
                    <p>{resource.url.split('/').pop()}</p>
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => handleDeleteResource(index)}
                    >
                      Delete Resource
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="button-container">
          <button type="submit" className="btn btn-primary">
            Save Changes
          </button>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="btn btn-secondary"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditAsset; 